/* eslint-disable no-underscore-dangle */
import { Anchor } from '@components/atoms/Anchor';
import Section from '@components/atoms/Section';
import BreadCrumbs from '@components/molecules/BreadCrumbs';
import PageContainer from '@components/molecules/PageContainer';
import RichTextSection from '@components/molecules/RichTextSection';
import { TableOfContents } from '@components/molecules/slices/TableOfContents';
import {
  ButtonOnRichText,
  SliceVehicleModels,
} from '@components/molecules/SliceZone';
import DefaultTable from '@components/molecules/table/DefaultTable';
import FixedGetQuoteButton from '@components/organisms/buttons/FixedGetQuoteButton';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import FAQBlock from '@components/organisms/seo/FAQBlock';
import { CoverageType } from '@graphql/generated';
import COLORS from '@lib/constants/colors';
import env from '@lib/constants/env';
import LINKS from '@lib/constants/links';
import ROUTES from '@lib/constants/routes';
import { aggregateRating } from '@lib/constants/seo';
import carriersContent from '@lib/data/contents/carriersContent';
import { integerToCurrency } from '@lib/helpers/misc';
import {
  getCoverageInfo,
  getInsurancePageInfo,
  InsurancePageId,
} from '@lib/helpers/texts';
import prisma from '@lib/services/prisma';
import { FipeModel, InsurancePrice } from '@prisma/client';
import { maxValue, minValue } from 'helpers';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ArticleJsonLd, ProductJsonLd } from 'next-seo';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import slugify from 'slugify';

interface Props {
  recentVehiclesWithInsurancePrice:
    | (FipeModel & {
        insurancePrices: InsurancePrice[];
      })[]
    | null;
  id: InsurancePageId;
  models: {
    model: string;
    modelSlug: string;
  }[];
  brand: string;
  groupInsurancePriceByYear:
    | {
        _avg: {
          theft: number | null;
          totalLoss: number | null;
        };
        modelYear: number;
      }[]
    | null;
}

const Brand = ({
  recentVehiclesWithInsurancePrice,
  id,
  models,
  brand,
  groupInsurancePriceByYear,
}: Props) => {
  const router = useRouter();

  const info = getInsurancePageInfo(id);

  if (router.isFallback || !brand) return <PageContainer loading />;

  const carriers = Object.values(carriersContent).filter((c) =>
    c.products.some((p) => p.pageId === id),
  );

  const averageInsurangePrice = groupInsurancePriceByYear?.map((item) => ({
    year: item.modelYear,
    avg: Math.round(((item._avg.theft || 0) + (item._avg.totalLoss || 0)) / 2),
  }));

  const cheapestYear = averageInsurangePrice
    ? minValue(averageInsurangePrice, 'avg')
    : null;
  const expensiveYear = averageInsurangePrice
    ? maxValue(averageInsurangePrice, 'avg')
    : null;

  const heads = [
    {
      id: 'h-1',
      text: `Modelos da ${brand} com ${info?.productName} mais barato`,
    },
    {
      id: 'h-2',
      text: `Tipos de ${info?.productName} ${brand}`,
    },
    {
      id: 'h-3',
      text: `O que cobre o seguro de ${info?.name} ${brand}?`,
    },
    {
      id: 'h-4',
      text: `Qual a melhor seguradora para ${brand}?`,
    },
    {
      id: 'h-5',
      text: `Como contratar o seguro de ${info?.name} ${brand} online?`,
    },
    {
      id: 'h-6',
      text: `Como renovar o seguro de ${info?.name} ${brand} online?`,
    },
    {
      id: 'h-7',
      text: `Custo do seguro ${brand} por ano do modelo`,
    },
    {
      id: 'h-8',
      text: `Confira quanto custa o seguro para ${info?.name}s ${brand}`,
    },
  ];

  return (
    <PageContainer
      title={`${info?.productName} ${brand} - Cotar/Renovar Online e Grátis`}
      metaDescription={`Simule o seguro para o seu ${info?.name} ${brand} online. Aqui na Luna Seguros você encontra os melhores preços de seguro ${brand} nas melhores seguradoras. Seguro ${brand} barato é na Luna.`}
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
      customAmpPage
    >
      <ArticleJsonLd
        url={`${env.baseUrl}/${ROUTES.insurancePage.brand(id, brand)}`}
        title={`${info?.productName} ${brand} - Cotar/Renovar Online e Grátis`}
        description={`Simule o seguro para o seu ${info?.name} ${brand} online. Aqui na Luna Seguros você encontra os melhores preços de seguro ${brand} nas melhores seguradoras. Seguro ${brand} barato é na Luna.`}
        datePublished="2021-10-10T03:00:00.000Z"
        dateModified="2021-11-28T03:00:00.000Z"
        authorName={['Luna Seguros']}
        publisherName="Luna Seguros"
        publisherLogo={LINKS.logo}
        images={[LINKS.ogImage]}
      />
      <ProductJsonLd
        productName={`${info?.productName} ${brand}`}
        description={`Cote o seguro para o seu ${info?.name} ${brand} online. Aqui na Luna Seguros você encontra os melhores preços de seguro ${brand} nas melhores seguradoras. Seguro ${brand} barato é na Luna.`}
        aggregateRating={aggregateRating}
      />
      <DefaultHeader
        title={`Melhor seguro de ${
          info?.name
        } da ${brand} em ${new Date().getFullYear()}`}
        subtitle={`Descubra se você está pagando caro no seguro de ${info?.name} em menos de dois minutos.`}
        buttonText={`Simular Seguro ${brand}`}
        pastel
      />

      <RichTextSection>
        <p>
          O seguro de {info?.name} {brand} é uma{' '}
          <Link href={ROUTES.insurancePage.home(id)}>
            <a>apólice de {info?.productName}</a>
          </Link>{' '}
          que protege um veículo {brand} de todos os tipos de riscos decorrentes
          de roubo, acidentes, desastres provocadas pelo homem, incêndios e
          desastres naturais, como desabamentos, ciclones, etc. Também protege o
          dono de {info?.name} contra qualquer responsabilidade contra
          terceiros, além de oferecer cobertura de acidentes pessoais para ele.
        </p>

        <TableOfContents
          slice={{ primary: { title1: 'Resumo' } }}
          heads={heads.filter((h) => {
            if (h.id === heads[0].id) {
              return recentVehiclesWithInsurancePrice;
            }

            if (h.id === heads[6].id) {
              return averageInsurangePrice;
            }

            return true;
          })}
        />
        {recentVehiclesWithInsurancePrice && (
          <>
            <h2 id={heads[0].id}>{heads[0].text}</h2>
            <p>
              Abaixo está uma lista com{' '}
              {recentVehiclesWithInsurancePrice.length} modelos mais recentes de{' '}
              {info?.name} {brand}, juntamente com seus preços de seguro:
            </p>
            <DefaultTable
              heads={[
                `${info?.name} ${brand}`,
                'Ano do modelo',
                'Valor médio do seguro',
              ]}
              rows={recentVehiclesWithInsurancePrice.map((v) => [
                <Link
                  key="link"
                  href={ROUTES.insurancePage.model(id, brand, v.modelSlug)}
                >
                  <a>
                    {brand} {v.model}
                  </a>
                </Link>,
                v.modelYear,
                integerToCurrency(
                  Math.round(
                    v.insurancePrices.reduce(
                      (acc, curr) => acc + curr.theft + curr.totalLoss,
                      0,
                    ) /
                      (v.insurancePrices.length * 2),
                  ),
                ),
              ])}
            />
          </>
        )}
        <h2 id={heads[1].id}>{heads[1].text}</h2>
        <p>
          O{' '}
          <Link href={ROUTES.insurancePage.carriers(id)}>
            <a>melhor seguro para {info?.name}s</a>
          </Link>{' '}
          está disponível em duas opções. Dê uma olhada em ambos os tipos de
          planos de seguro {brand}:
        </p>
        <ul>
          <li>
            <strong>
              {info?.productName} {brand} de Terceiros
            </strong>{' '}
            - É uma de apólice de seguro {brand} que cobre apenas as
            responsabilidades contra terceiros do dono do veículo {brand} no
            caso dele causar lesões corporais, morte ou danos materiais a
            terceiros durante um acidente. Algumas seguradoras de {info?.name}{' '}
            também podem fornecer cobertura de acidentes pessoais para o
            condutor de {info?.name} {brand}.
          </li>
          <li>
            <strong>
              Seguro de {info?.name} {brand} Compreensivo
            </strong>{' '}
            - É o tipo de apólice de seguro {brand} que oferece cobertura de
            danos próprios para o {info?.name} {brand}, além da cobertura de
            seguro {brand} de terceiros. Ele protege o {info?.name}
            {brand} de qualquer perda ou dano devido a acidentes, roubo,
            desastres provocados pelo homem (como motins, greves, etc.),
            incêndios e desastres naturais (como inundações, terremotos, etc.).
            Todos os planos de seguro abrangentes da {brand} oferecem cobertura
            de acidentes pessoais para o proprietário-motorista do {info?.name}.
          </li>
        </ul>
        <h2 id={heads[2].id}>{heads[2].text}</h2>
        <p>
          Os riscos a seguir são cobertos por uma apólice de {info?.productName}{' '}
          {brand}:
        </p>
        <ul>
          {' '}
          {[
            CoverageType.Colision,
            CoverageType.Theft,
            CoverageType.Fire,
            CoverageType.Nature,
            CoverageType.TotalLoss,

            CoverageType.ThirdPartyMaterial,
            CoverageType.ThirdPartyBody,
            CoverageType.MoralDamages,
            CoverageType.AppDeath,
            CoverageType.AppInvalid,
          ].map((c) => (
            <li key={c}>{getCoverageInfo({ type: c, value: 1 }).name}</li>
          ))}
        </ul>
        <h2 id={heads[3].id}>{heads[3].text}</h2>
        <p>
          As melhores{' '}
          <Link href={ROUTES.insurancePage.carriers(id)}>
            <a>
              seguradoras de {info?.name} para {brand}
            </a>
          </Link>{' '}
          dependem de vários fatores, principalmente que tipo de cobertura você
          está buscando e como você usa o veículo.
        </p>
        {id === 'seguro-moto' && (
          <>
            <Link href="/blog/melhores-seguradoras-para-motos">
              <a>Confira o ranking do melhor seguro de moto em 2022</a>
            </Link>
          </>
        )}
        <p>As seguradoras que fazem seguro para {brand} são: </p>
        <ul>
          {carriers.map((c) => (
            <li key={c.id}>
              <Link href={ROUTES.insurancePage.carrier(id, c.id)}>
                <a>
                  Seguro {brand} {c.name}
                </a>
              </Link>
            </li>
          ))}
        </ul>
        <div>
          <h2 id={heads[4].id}>{heads[4].text}</h2>
          <p>
            Como um dono de {info?.name} {brand}, você pode facilmente contratar
            um {info?.productName} online. Basta seguir os passos abaixo para
            contratar o{' '}
            <Link href={ROUTES.insurancePage.home(id)}>
              <a>melhor seguro de {info?.name}</a>
            </Link>{' '}
            da {brand} online:
          </p>
          <ul>
            <li>
              <Link href={ROUTES.startForm()}>
                <a>Inicie a contratação do seguro de {info?.name} online</a>
              </Link>
            </li>
            <li>Informe os detalhes de como você usa o veículo {brand}</li>
            <li>Responda sobre quem é o condutor da {brand}</li>
            <li>Informe os dados de contato</li>
            <li>
              As melhore seguradoras de {info?.name} {brand} serão mostradas
            </li>
            <li>
              Escolha o melhor seguro de {info?.name} {brand} conforme a sua
              necessidade
            </li>
            <li>
              Realize o pagamento do seguro de {info?.name} via cartão de
              crédito
            </li>
            <li>
              A sua seguradora de {info?.name} {brand} vai emitir a sua apólice
              online 🎉
            </li>
          </ul>
          <ButtonOnRichText
            href={ROUTES.startForm()}
            text={`Cotar Seguro ${brand}`}
          />
        </div>
        <h2 id={heads[5].id}>{heads[5].text}</h2>
        <p>
          Cada apólice de {info?.productName} {brand} tem uma data de vigência.
          O dono do {info?.name} deve se lembrar de renovar sua apólice de
          seguro
          {brand} antes dessa data de validade para continuar a proteger seu{' '}
          {info?.name}. A renovação do seguro de {info?.name} pode ser feita
          online seguindo o procedimento abaixo:
        </p>
        <ul>
          <li>
            <Link href={ROUTES.startForm()}>
              <a>Inicie a renovação do seguro {brand} online</a>
            </Link>
          </li>
          <li>Informe sua seguradora da sua última apólice {brand}</li>
          <li>Verifique o número da sua apólice de seguro {brand}</li>
          <li>Informe sua classe bônus atual</li>
          <li>
            Escolha a melhor opção de renovação de {info?.productName} {brand}
          </li>
          <li>
            A sua nova seguradora de {info?.name} {brand} vai renovar a sua
            apólice online 🎉
          </li>
        </ul>
        <ButtonOnRichText
          href={ROUTES.startForm()}
          text={`Renovar Seguro ${brand}`}
        />
        {averageInsurangePrice && (
          <>
            <h2 id={heads[6].id}>{heads[6].text}</h2>
            <p>
              Uma nova máquina da {brand} novinha em folha é definitivamente
              atraente, mas você não encontrará nenhuma economia quando se trata
              de seu {info?.productName}. Graças à desvalorização, os modelos
              mais antigos tendem a ser mais baratos para colocar seguro.
            </p>
            <p>
              De acordo com a{' '}
              <Link href={ROUTES.fipe.home}>
                <a>Tabela Fipe</a>
              </Link>
              , um {info?.name} novo perde 23,5% de seu valor no primeiro ano e
              60% de seu valor nos primeiros cinco anos. E quanto menos vale o
              seu {info?.name}, mais barato é para consertá-lo ou substituí-lo
              se você precisar abrir um sinistro. Para você, isso significa
              economizar no seguro.
            </p>
            <p>
              Os donos de {info?.name}s mais modernas também tendem a alugar ou
              financiar seus veículos e, portanto, devem contratar um{' '}
              {info?.productName} que cubra tudo. Você sempre pagará mais pela
              cobertura total do que apenas uma cobertura contra roubo e furto.
            </p>
            <p>
              De acordo com os dados internos da Luna, as {info?.name} mais
              barata para segurar da {brand} são os modelos de{' '}
              {cheapestYear?.year}. O ano mais caro para segurar {brand} são os
              modelos de {expensiveYear?.year}.
            </p>
            <DefaultTable
              scroll
              heads={['Ano do Modelo', 'Preço Médio Seguro']}
              rows={averageInsurangePrice
                ?.sort((a, b) => b.year - a.year)
                .map((item) => [item.year, integerToCurrency(item.avg)])}
            />
          </>
        )}
        <h2 id={heads[7].id}>{heads[7].text}</h2>
        <SliceVehicleModels
          slice={{
            models,
            brand,
            pageId: id,
          }}
        />
        <FAQBlock
          title={`Perguntas frequentes seguro ${brand}`}
          items={[
            {
              question: `Quanto tempo demora para renovar um seguro de ${info?.name} ${brand}?`,
              answer: (
                <>
                  <p>
                    Você pode{' '}
                    <Link passHref href={ROUTES.insurancePage.home(id)}>
                      <Anchor>renovar um seguro de {info?.name}</Anchor>
                    </Link>{' '}
                    {brand} em menos de 5 minutos com a Luna
                  </p>
                </>
              ),
            },
            {
              question: `Onde posso contratar seguro de ${info?.name} ${brand}`,
              answer: (
                <>
                  <p>
                    Você pode visitar nosso site{' '}
                    <Link passHref href={ROUTES.root}>
                      <Anchor>luna.ac</Anchor>
                    </Link>
                    , comparar todas as cotações disponíveis para {info?.name}s{' '}
                    {brand} e selecionar a que melhor se adapta às suas
                    necessidades. Com a Luna, você consegue contratar um seguro
                    totalmente online.
                  </p>
                </>
              ),
            },
            {
              question: `Como posso contratar um seguro online de ${info?.name}s da ${brand}`,
              answer: (
                <>
                  <p>
                    Você pode contratar um{' '}
                    <Link passHref href={ROUTES.insurancePage.home(id)}>
                      <Anchor>seguro online para {info?.name}s</Anchor>
                    </Link>{' '}
                    da {brand} visitando o site da Luna. O nosso site compara os
                    preços de seguros das melhores seguradoras para você
                    escolher a melhor opção.
                  </p>
                </>
              ),
            },
            {
              question: `Eu posso renovar um seguro de ${info?.name} da ${brand} depois de vencido?`,
              answer: (
                <>
                  <p>
                    Sim, se a apólice de seguro de {info?.name} da {brand}{' '}
                    estiver vencida, você ainda pode renová-la dentro do período
                    de carência de 90 dias a partir da data de vencimento, que
                    varia de seguradora para seguradora.
                  </p>
                </>
              ),
            },
          ]}
        />
      </RichTextSection>
      <Section style={{ paddingTop: 0 }}>
        <BreadCrumbs
          links={[
            {
              label: 'Luna',
              href: ROUTES.root,
            },
            {
              label: info?.productName || '',
              href: ROUTES.insurancePage.home(id),
            },
            {
              label: brand,
              href: ROUTES.insurancePage.brand(id, brand),
            },
          ]}
        />
      </Section>
      <FixedGetQuoteButton />
    </PageContainer>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: ['/seguro-carro/bmw', '/seguro-moto/honda'],
  fallback: true,
});

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { insurancePage, brand: brandName } = params as {
    insurancePage: InsurancePageId;
    brand: string;
  };

  const brand = slugify(brandName, { lower: true });

  const info = getInsurancePageInfo(insurancePage);

  if (!brand || !info) return { notFound: true };

  const [recentVehiclesWithInsurancePrice, models, groupInsurancePriceByYear] =
    await Promise.all([
      prisma.fipeModel.findMany({
        where: {
          brandSlug: brand,

          vehicleType: info.type,
          insurancePrices: {
            some: {
              uf: { equals: 'SP' },
            },
          },
        },
        include: {
          insurancePrices: true,
        },
        take: 10,
        distinct: ['fipeCode'],
        orderBy: { modelYear: 'desc' },
      }),
      prisma.fipeModel.findMany({
        where: {
          brandSlug: brand,

          vehicleType: info.type,
        },
        distinct: ['fipeCode'],
        orderBy: { model: 'asc' },
        select: {
          model: true,
          brand: true,
          modelSlug: true,
        },
      }),
      prisma.insurancePrice.groupBy({
        by: ['modelYear'],
        where: {
          vehicle: {
            brandSlug: brand,

            vehicleType: info.type,
          },
        },
        _avg: {
          theft: true,
          totalLoss: true,
        },
      }),
    ]);

  if (models?.length === 0) return { notFound: true };

  return {
    props: {
      recentVehiclesWithInsurancePrice: recentVehiclesWithInsurancePrice.length
        ? recentVehiclesWithInsurancePrice
        : null,
      models,
      id: insurancePage,
      brand: models[0].brand,
      groupInsurancePriceByYear: groupInsurancePriceByYear.length
        ? groupInsurancePriceByYear
        : null,
    },
    revalidate: 3600,
  };
};

export default Brand;
